/**
 * Layout da tela de Conversas, no desenho do WhatsApp Web com os tokens do
 * Pharu: lista à esquerda, conversa à direita, cada painel rolando sozinho e
 * a página sem rolagem. Neutro em tudo; cor só no sinal de estado.
 */
import styled, { css, keyframes } from "styled-components";

import { BP_MOBILE } from "../../../styles/canonical/breakpoints";
import { FundoDaConversa } from "../Mensagens";
import "./conversa.css";

export const CELULAR = `(max-width: ${BP_MOBILE - 1}px)`;
const tom = (nome, parte) => `var(--${nome || "neutral"}-${parte})`;

/* Ocupa a área livre abaixo do cabeçalho do app (a rota é full-bleed). O
   respiro é padding, não margem: margem vazaria e criaria a rolagem. */
export const Tela = styled.div`
  box-sizing: border-box;
  height: calc(100dvh - var(--topbar-height) - var(--safe-area-top, 0px) - var(--mfa-banner-h, 0px));
  min-height: 420px;
  padding: 16px;

  @media ${CELULAR} {
    padding: 0;
  }
`;

export const Moldura = styled.div`
  display: flex;
  height: 100%;
  min-width: 0;
  border: 1px solid var(--border);
  border-radius: var(--radius-2xl, 16px);
  overflow: hidden;
  background: var(--surface);

  @media ${CELULAR} {
    border: 0;
    border-radius: 0;
  }
`;

export const ColunaLista = styled.section`
  display: flex;
  flex-direction: column;
  flex: 0 0 clamp(300px, 34%, 380px);
  width: clamp(300px, 34%, 380px);
  min-height: 0;
  min-width: 0;
  border-right: 1px solid var(--border);
  background: var(--surface);

  /* No celular a lista fica montada (e com a rolagem guardada) enquanto a conversa está aberta. */
  &[hidden] {
    display: none;
  }

  @media ${CELULAR} {
    flex-basis: 100%;
    width: 100%;
    border-right: 0;
  }
`;

/** `cp-conversa` liga as container queries de `conversa.css`. */
export const ColunaConversa = styled.section.attrs({ className: "cp-conversa" })`
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-width: 0;
  min-height: 0;
`;

/* ---------- Botões compactos (34 px) ---------- */

const botao = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  flex: 0 0 auto;
  height: 34px;
  min-width: 34px;
  padding: 0 11px;
  border-radius: 9px;
  border: 1px solid var(--border);
  background: var(--surface);
  color: var(--ink);
  font: inherit;
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;
  transition: background-color 120ms ease-out, transform 120ms ease-out;

  &:hover:not(:disabled) {
    background: var(--surface-2);
  }

  &:active:not(:disabled) {
    transform: scale(0.97);
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: 2px;
  }

  svg {
    flex: 0 0 auto;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;

    &:active:not(:disabled) {
      transform: none;
    }
  }
`;

export const BotaoCompacto = styled.button`
  ${botao}
`;

/** Só ícone, sem borda (voltar, recolher). */
export const BotaoIcone = styled.button`
  ${botao}
  padding: 0;
  width: 34px;
  border-color: transparent;
  background: transparent;
  color: var(--ink-2);

  &:hover:not(:disabled) {
    color: var(--ink);
  }
`;

/** Ação dentro de uma frase ("Tentar de novo"): texto sublinhado, sem caixa. */
export const BotaoDeTexto = styled.button`
  padding: 0;
  border: 0;
  background: none;
  color: inherit;
  font: inherit;
  font-weight: 600;
  text-decoration: underline;
  text-underline-offset: 2px;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: 2px;
    border-radius: 3px;
  }
`;

/* ---------- Avatar neutro ---------- */

export const Avatar = styled.span`
  display: inline-grid;
  place-items: center;
  flex: 0 0 auto;
  width: ${({ $tamanho = 46 }) => $tamanho}px;
  height: ${({ $tamanho = 46 }) => $tamanho}px;
  border-radius: 50%;
  background: var(--border-strong);
  color: var(--ink-2);
  font-size: ${({ $tamanho = 46 }) => Math.round($tamanho * 0.34)}px;
  font-weight: 600;
  letter-spacing: 0.02em;
  user-select: none;
`;

/* ---------- Lista ---------- */

export const TopoDaLista = styled.header`
  display: grid;
  gap: 10px;
  padding: 12px 16px 10px;
  border-bottom: 1px solid var(--divider);
`;

export const LinhaDoTitulo = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  margin-left: -6px;

  h1 {
    flex: 1 1 auto;
    min-width: 0;
    margin: 0;
    font-size: 19px;
    font-weight: 700;
    letter-spacing: -0.01em;
    color: var(--ink);
  }
`;

export const Busca = styled.label`
  position: relative;
  display: block;

  > svg {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: var(--ink-3);
    pointer-events: none;
  }

  input {
    box-sizing: border-box;
    width: 100%;
    height: 38px;
    padding: 0 36px 0 38px;
    border-radius: 10px;
    border: 1px solid transparent;
    background: var(--surface-2);
    color: var(--ink);
    font: inherit;
    font-size: 14px;

    &::placeholder {
      color: var(--ink-3);
    }

    /* O "x" nativo do campo de busca some: o nosso limpa e tem foco visível. */
    &::-webkit-search-cancel-button {
      -webkit-appearance: none;
      appearance: none;
    }

    &:focus {
      outline: none;
      border-color: var(--accent);
      background: var(--surface);
      box-shadow: var(--shadow-focus);
    }
  }

  button {
    position: absolute;
    right: 4px;
    top: 50%;
    transform: translateY(-50%);
    display: grid;
    place-items: center;
    width: 30px;
    height: 30px;
    border: 0;
    border-radius: 8px;
    background: transparent;
    color: var(--ink-3);
    cursor: pointer;

    &:hover {
      color: var(--ink);
    }

    &:focus-visible {
      outline: 2px solid var(--accent);
    }
  }
`;

/* Sete filtros cabem em duas linhas na coluna da lista (e no celular). */
export const Filtros = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
`;

export const Filtro = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  height: 30px;
  padding: 0 10px;
  border-radius: 999px;
  border: 1px solid ${({ $ativo }) => ($ativo ? "var(--ink)" : "var(--border)")};
  background: ${({ $ativo }) => ($ativo ? "var(--ink)" : "var(--surface)")};
  color: ${({ $ativo }) => ($ativo ? "var(--surface)" : "var(--ink-2)")};
  font: inherit;
  font-size: 12.5px;
  font-weight: ${({ $ativo }) => ($ativo ? 600 : 500)};
  white-space: nowrap;
  cursor: pointer;
  transition: background-color 120ms ease-out, color 120ms ease-out;

  &:hover {
    border-color: ${({ $ativo }) => ($ativo ? "var(--ink)" : "var(--border-strong)")};
  }

  &:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: 2px;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

/** Contagem do filtro: neutra; "A classificar" com fila fica em warning. */
export const Contagem = styled.span`
  font-size: 12px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  ${({ $tom, $ativo }) =>
    $tom && !$ativo
      ? css`
          min-width: 18px;
          padding: 0 5px;
          border-radius: 9px;
          line-height: 18px;
          text-align: center;
          background: ${tom($tom, "bg")};
          color: ${tom($tom, "text")};
        `
      : css`
          opacity: 0.7;
        `}
`;

export const Rolagem = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  overscroll-behavior: contain;
`;

export const ItensDaLista = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`;

/** A linha segura o botão da prévia (irmão do botão da linha: botão dentro de botão não pode). */
export const LinhaDaLista = styled.li`
  position: relative;
`;

/* Altura da linha: nome, última mensagem e a frase da IA. */
const ALTURA_DA_LINHA = 86;

export const ItemDaLista = styled.button`
  display: grid;
  grid-template-columns: 46px minmax(0, 1fr);
  align-items: center;
  gap: 12px;
  width: 100%;
  height: ${ALTURA_DA_LINHA}px;
  padding: 0 0 0 14px;
  border: 0;
  background: ${({ $ativo }) => ($ativo ? "var(--surface-3)" : "transparent")};
  color: inherit;
  font: inherit;
  text-align: left;
  cursor: pointer;

  &:hover {
    background: ${({ $ativo }) => ($ativo ? "var(--surface-3)" : "var(--surface-2)")};
  }

  &:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: -2px;
  }
`;

export const TextoDoItem = styled.span`
  display: grid;
  gap: 2px;
  align-content: center;
  height: 100%;
  min-width: 0;
  /* Folga à direita para a coluna do botão da prévia. */
  padding-right: 52px;
  border-bottom: 1px solid var(--divider);

  li:last-child & {
    border-bottom-color: transparent;
  }
`;

export const LinhaDoItem = styled.span`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

export const NomeDoItem = styled.span`
  flex: 1 1 auto;
  min-width: 0;
  font-size: 14.5px;
  font-weight: 600;
  color: var(--ink);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const HoraDoItem = styled.span`
  flex: 0 0 auto;
  font-size: 12px;
  color: var(--ink-3);
  font-variant-numeric: tabular-nums;
`;

export const UltimaMensagem = styled.span`
  display: flex;
  align-items: center;
  gap: 4px;
  flex: 1 1 auto;
  min-width: 0;
  /* Nada passa por baixo do selo ao lado (autor comprido como "Wesley Obras e Medição"). */
  overflow: hidden;
  font-size: 13px;
  color: var(--ink-3);

  svg {
    flex: 0 0 auto;
  }

  > span {
    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  /* O autor ocupa até metade da linha; o resto fica para a mensagem. */
  > .autor {
    flex: 0 0 auto;
    max-width: 50%;
  }

  > .texto {
    flex: 1 1 0;
  }
`;

/* A frase da IA: discreta e sem autor, mais escura que a última mensagem (é o que importa). */
export const LinhaDaIA = styled.span`
  display: block;
  min-width: 0;
  font-size: 12.5px;
  line-height: 1.4;
  font-weight: 400;
  color: ${({ $vazia }) => ($vazia ? "var(--ink-4)" : "var(--ink-2)")};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

/** Abre a prévia sem abrir a conversa: numa coluna própria à direita da linha, no meio da altura. */
export const BotaoDaPrevia = styled.button`
  position: absolute;
  right: 10px;
  top: 50%;
  display: grid;
  place-items: center;
  width: 34px;
  height: 34px;
  margin-top: -17px;
  padding: 0;
  border: 1px solid ${({ $aberto }) => ($aberto ? "var(--border-strong)" : "transparent")};
  border-radius: 9px;
  background: ${({ $aberto }) => ($aberto ? "var(--surface)" : "transparent")};
  color: ${({ $aberto }) => ($aberto ? "var(--ink)" : "var(--ink-3)")};
  cursor: pointer;
  transition: background-color 120ms ease-out, color 120ms ease-out, transform 120ms ease-out;

  &:hover {
    background: var(--surface);
    border-color: var(--border);
    color: var(--ink);
  }

  &:active {
    transform: scale(0.95);
  }

  &:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: -2px;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;

    &:active {
      transform: none;
    }
  }
`;

export const Contador = styled.span`
  flex: 0 0 auto;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  border-radius: 10px;
  display: inline-grid;
  place-items: center;
  font-size: 11.5px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  background: ${({ $tom }) => tom($tom, "bg")};
  color: ${({ $tom }) => tom($tom, "text")};
  box-shadow: inset 0 0 0 1px ${({ $tom }) => tom($tom, "border")};
`;

export const PontoDeEstado = styled.span`
  flex: 0 0 auto;
  width: 10px;
  height: 10px;
  margin-right: 5px;
  border-radius: 50%;
  background: ${({ $tom }) => tom($tom, "solid")};
`;

export const AvisoDaLista = styled.p`
  margin: 0;
  padding: 28px 24px;
  text-align: center;
  font-size: 13.5px;
  line-height: 1.5;
  color: var(--ink-3);
  text-wrap: pretty;
`;

/* ---------- Esqueleto de carga ---------- */

const brilho = keyframes`
  from { opacity: 0.55; }
  to { opacity: 1; }
`;

export const Osso = styled.span`
  display: block;
  height: ${({ $altura = 10 }) => $altura}px;
  width: ${({ $largura = "100%" }) => $largura};
  border-radius: 6px;
  background: var(--surface-3);
  animation: ${brilho} 900ms ease-in-out infinite alternate;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export const ItemEsqueleto = styled.li`
  display: grid;
  grid-template-columns: 46px minmax(0, 1fr);
  align-items: center;
  gap: 12px;
  height: ${ALTURA_DA_LINHA}px;
  padding: 0 14px;

  > span:last-child {
    display: grid;
    gap: 8px;
  }
`;

/* ---------- Conversa ---------- */

export const Cabecalho = styled.header.attrs({ className: "cp-cabecalho" })`
  position: relative;
  z-index: 3;
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  height: 60px;
  padding: 0 12px 0 16px;
  border-bottom: 1px solid var(--border);
  background: var(--surface);

  @media ${CELULAR} {
    padding-left: 4px;
    gap: 8px;
  }
`;

export const IdentidadeDaConversa = styled.div`
  flex: 1 1 auto;
  min-width: 0;

  h1,
  h2 {
    margin: 0;
    font-size: 15.5px;
    font-weight: 600;
    line-height: 1.25;
    color: var(--ink);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  p {
    margin: 1px 0 0;
    font-size: 12.5px;
    line-height: 1.3;
    color: var(--ink-3);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

/* Embaixo do nome: o selo "Sem marcar como lida" (fixo) e o tipo, pedido e cliente (cortado se faltar lugar). */
export const LinhaDeBaixoDaConversa = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  margin-top: 3px;

  > p {
    flex: 1 1 auto;
    min-width: 0;
    margin: 0;
  }
`;

export const AcoesDoCabecalho = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex: 0 0 auto;
`;

export const FaixaDeSugestao = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px 8px;
  padding: 8px 16px;
  border-bottom: 1px solid var(--border);
  background: var(--surface-2);
  font-size: 13px;
  color: var(--ink-2);

  > span {
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
`;

export const AreaDasMensagens = styled(FundoDaConversa)`
  position: relative;
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  overscroll-behavior: contain;
  padding: 8px clamp(14px, 6%, 72px) 4px;
`;

export const NotaCentral = styled.p`
  align-self: center;
  max-width: 440px;
  margin: 8px auto 12px;
  padding: 6px 12px;
  border-radius: 8px;
  background: var(--cp-balao, var(--surface));
  box-shadow: var(--shadow-1);
  font-size: 12px;
  line-height: 1.45;
  text-align: center;
  color: var(--ink-2);
  text-wrap: pretty;

  svg {
    vertical-align: -2px;
    margin-right: 4px;
  }
`;

export const Rodape = styled.footer`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 46px;
  padding: 8px 16px calc(8px + var(--safe-area-bottom, 0px));
  border-top: 1px solid var(--border);
  background: var(--surface);
  font-size: 12.5px;
  color: var(--ink-3);
  text-align: center;
`;

export const Vazia = styled.div`
  flex: 1 1 auto;
  display: grid;
  place-items: center;
  padding: 32px;
  background: var(--surface-2);
  text-align: center;

  > div {
    display: grid;
    justify-items: center;
    gap: 10px;
    max-width: 380px;
  }

  h2 {
    margin: 6px 0 0;
    font-size: 20px;
    font-weight: 600;
    letter-spacing: -0.01em;
    color: var(--ink);
    text-wrap: balance;
  }

  p {
    margin: 0;
    font-size: 14px;
    line-height: 1.55;
    color: var(--ink-2);
    text-wrap: pretty;
  }

  small {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin-top: 18px;
    font-size: 12.5px;
    color: var(--ink-3);
  }
`;

export const IconeDaVazia = styled.span`
  display: grid;
  place-items: center;
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: var(--surface);
  border: 1px solid var(--border);
  color: var(--ink-3);
`;

/* ---------- Popover (menu do tipo e busca do cliente) ---------- */

export const Ancora = styled.div`
  position: relative;
  display: inline-flex;
`;

export const Popover = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  z-index: 20;
  width: ${({ $largura = 300 }) => $largura}px;
  max-width: calc(100vw - 24px);
  padding: 6px;
  border-radius: 12px;
  border: 1px solid var(--border);
  background: var(--surface);
  box-shadow: var(--shadow-3);
  transform-origin: top right;
  animation: ${keyframes`
    from { opacity: 0; transform: scale(0.97) translateY(-2px); }
    to { opacity: 1; transform: none; }
  `} 140ms cubic-bezier(0.23, 1, 0.32, 1);

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }

  /* No celular o botão fica no meio do cabeçalho: o popover ocupa a largura
     da tela, logo abaixo dele, em vez de vazar pela esquerda. */
  @media ${CELULAR} {
    position: fixed;
    top: calc(var(--topbar-height) + var(--safe-area-top, 0px) + var(--mfa-banner-h, 0px) + 64px);
    left: 12px;
    right: 12px;
    width: auto;
    max-height: calc(100dvh - var(--topbar-height) - var(--safe-area-top, 0px) - 96px);
    overflow-y: auto;
    transform-origin: top center;
  }
`;

export const RotuloDoPopover = styled.div`
  padding: 8px 10px 4px;
  font-size: 12px;
  font-weight: 600;
  color: var(--ink-3);
`;

export const OpcaoDoMenu = styled.button`
  display: grid;
  grid-template-columns: 18px minmax(0, 1fr);
  gap: 2px 8px;
  align-items: start;
  width: 100%;
  padding: 8px 10px;
  border: 0;
  border-radius: 8px;
  background: ${({ $destaque }) => ($destaque ? "var(--surface-2)" : "transparent")};
  color: var(--ink);
  font: inherit;
  text-align: left;
  cursor: pointer;

  &:hover {
    background: var(--surface-2);
  }

  &:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: -2px;
  }

  > svg {
    grid-row: span 2;
    margin-top: 2px;
    color: var(--ink);
  }

  b {
    font-size: 13.5px;
    font-weight: 600;
    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  small {
    grid-column: 2;
    font-size: 12px;
    line-height: 1.35;
    color: var(--ink-3);
  }
`;

export const Divisoria = styled.hr`
  margin: 6px 4px;
  border: 0;
  border-top: 1px solid var(--divider);
`;
